import { ChatbotResources, FunctionType, STATUS, UserType } from '../types';
import { INITIAL_CHATBOT_MESSAGE } from './config';
import { getPromptIdFromName, getValidToken } from '../../src/utils';

// Function to reset the chatbot
export const resetChatbot = (props: ChatbotResources) => {
  const {
    createChatBotMessage,
    setState,
    setResponseDetailsSources,
    setSourceHistory,
    setExportStoryInProgress,
    setStoryResponses,
    setResetApp,
    setChatHistory,
    setDownloadChatHistory,
    setChatPromptResponseHistory,
    setIsChatHistoryActive,
    chatWindowConversations,
    setChatWindowConversations,
  } = props;

  const botMessage = createChatBotMessage(INITIAL_CHATBOT_MESSAGE, { widget: 'initWidget' }); // initial message
  setState(() => ({
    messages: [botMessage],
    intIntent: '',
    intPlan: [],
    currentStep: '',
    isFirstResponse: false,
    showResponseSugestionsPopup: false,
  }));
  setResponseDetailsSources({});
  setSourceHistory({});
  setExportStoryInProgress(false);
  setStoryResponses([]);
  setResetApp(false); // Turn off flag
  setChatHistory(`AI: ${INITIAL_CHATBOT_MESSAGE}\n`); // Reset history
  setDownloadChatHistory(`AI: ${INITIAL_CHATBOT_MESSAGE}\n`);
  setChatPromptResponseHistory([]); //State to hold only user generated prompts w/o init message.
  setIsChatHistoryActive(false);
  if (chatWindowConversations.length) {
    setChatWindowConversations([]);
  }
};

// Function to scroll chat to bottom after specified time (in ms)
export const scrollToBottom = (scrollend: string, time: number) => {
  const chatBox = document.querySelector('.react-chatbot-kit-chat-message-container');
  if (chatBox) {
    if (!scrollend) {
      setTimeout(() => {
        chatBox.scrollTop = chatBox.scrollHeight;
      }, time || 1000);
    } else {
      const promptDiv = document.querySelector(scrollend);
      if (promptDiv) {
        const promptRect = promptDiv.getBoundingClientRect();
        const chatBoxRect = chatBox.getBoundingClientRect();
        const scrollAmount = promptRect.bottom - chatBoxRect.top - chatBox.clientHeight;
        chatBox.scrollTop = Math.max(chatBox.scrollTop + scrollAmount, 0);
      }
    }
  }
};

// Function to hide the suggested responses when message is sent
export const hideSuggestedResponses = (isFirstResponseRecieved: boolean) => {
  document.querySelectorAll('.initial-suggestion-container').forEach((button) => {
    button.classList.add('hidden');
  });

  if (isFirstResponseRecieved) {
    document.querySelectorAll('.suggested-responses-container').forEach((button) => {
      button.classList.add('hidden');
    });
  }
};

// Function to hide the suggested responses popup
export const suggestedResponsesImg = document.createElement('img');
export const hideSuggestedPopUp = () => {
  document.querySelectorAll('.response-suggestions-popup-container').forEach((button) => {
    button.classList.add('hidden');
  });
  suggestedResponsesImg.setAttribute('src', '/icons/BulbIcon.svg');
};

// Function to handle the user message in the chat
export const userMessageHelper = async (
  {
    createChatBotMessage,
    setState,
    chatHistory,
    websocket,
    userMessage,
    selectedProject,
    selectedTranscript,
    setCurrentGenArcPromptAnalyticsUId,
    setResponseDetailsSources,
  }: ChatbotResources,
  message: string,
  enterpriseId: string,
  prompts: any,
  openAI: boolean,
  genAI: boolean,
  chatSessionId: string,
  genArcPromptAnalyticsUId: string,
  isPreDefinedPrompt?: boolean,
  setSearchBatches?: (batches: any[]) => void,
  setBatchType?: (type: string) => void
) => {
  const loadingMessage = createChatBotMessage('Great! Let me think...', {
    widget: 'intSteps',
  });
  setState((state: any) => ({
    ...state,
    messages: [...state.messages, loadingMessage],
    intIntent: 'Sending prompt to the AI',
  }));
  // Scroll to bottom of chatbot, delay of 1 second
  scrollToBottom('', 1000);

  setCurrentGenArcPromptAnalyticsUId(genArcPromptAnalyticsUId);
  setResponseDetailsSources({ showCitation: false, sourceList: [] });
  if (setSearchBatches) {
    setSearchBatches([]);
  }
  if (setBatchType) {
    setBatchType('');
  }
  const token = await getValidToken();

  // Setting up the request body to be sent to backend
  const request = {
    function: 'chat',
    action: 'sendMessage',
    prompt: message,
    history: chatHistory,
    userId: localStorage.getItem('gamma-userid') || crypto.randomUUID(),
    LoggedInUser: enterpriseId,
    PreDefinedPromptUId: getPromptIdFromName(prompts, message),
    UserPrompt: isPreDefinedPrompt ? null : message,
    OpenAI: openAI,
    GenAI: genAI,
    SessionUId: chatSessionId,
    projectName: selectedProject,
    GenArcPromptAnalyticsUId: genArcPromptAnalyticsUId,
    sourceName: selectedTranscript,
    authorization: `Bearer ${token}`,
  };

  // WebSocket code
  try {
    console.log('Request: ', request);
    websocket?.send('SendMessage', request);
    userMessage.current = message; // Save message for later use
  } catch {
    // Catch any errors fetching or parsing
    const botErrorMessage = createChatBotMessage(
      "I'm sorry. An error has occurred. Please try rephrasing your request.",
      {}
    );

    // Update the message history to show
    setState((prev: any) => ({
      ...prev,
      messages: [...prev.messages.slice(0, -1), botErrorMessage], // include image message if it exists
      // messages: [...prev.messages, botErrorMessage],
      intIntent: '',
      intPlan: [],
      currentStep: '',
    }));
  }
};

// Function to handle user actions that result in a chat message
// (i.e. Suggested Responses)
export const userActionHelper = (
  chatbotResources: ChatbotResources,
  message: string,
  enterpriseId: string,
  prompts: any,
  openAI: boolean,
  genAI: boolean,
  chatSessionId: string,
  genArcPromptAnalyticsUId: string,
  setSearchBatches?: (batches: any[]) => void,
  setBatchType?: (type: string) => void
) => {
  const { createClientMessage, setState } = chatbotResources;
  // Create message bubble
  const clientMessage = createClientMessage(message);
  // Update the state to show message bubble
  setState((prev: any) => ({
    ...prev,
    messages: [...prev.messages, clientMessage],
    showResponseSugestionsPopup: false,
  }));
  // Scroll to bottom of chatbot, delay of .1 second
  scrollToBottom('', 100);
  // Handle the user's message
  userMessageHelper(
    chatbotResources,
    message,
    enterpriseId,
    prompts,
    openAI,
    genAI,
    chatSessionId,
    genArcPromptAnalyticsUId,
    true,
    setSearchBatches,
    setBatchType
  );
};

// Function to handle Chat Responses (excluding Step Responses)
export const chatResponseHelper = (chatbotResources: ChatbotResources, parsedResponse: any) => {
  // Gather the resources
  const {
    setState,
    chatHistory,
    setChatHistory,
    userMessage,
    createChatBotMessage,
    setSelectedResponseId,
    setResponseDetailsSources,
    setSourceHistory,
    setGenArcHistoryPrompts,
    setDownloadChatHistory,
    setChatWindowConversations,
    isChatHistoryActive,
  } = chatbotResources;

  // Get the chat response from the API response
  const chatResponse = parsedResponse.chatResponse;
  console.log('chatResponse', chatResponse);
  setGenArcHistoryPrompts((genArcHistoryPrompts: any) => ({
    ...genArcHistoryPrompts,
    [parsedResponse?.chatResponse?.responseId]: userMessage.current,
  }));

  // Update chat history
  const newChatHistory =
    chatHistory +
    UserType.HUMAN +
    ': ' +
    userMessage.current +
    '\n' +
    UserType.AI +
    ': ' +
    parsedResponse.chatResponse.text +
    '\n' +
    UserType.SOURCE +
    ': ' +
    parsedResponse.chatResponse.responseSource +
    '\n';

  setChatHistory(newChatHistory); // Update the chat history state
  setDownloadChatHistory(newChatHistory);

  // Array to hold bot messages that get passed into the chat state
  const botMessages: any[] = [];

  // Create bot message
  botMessages.push(
    createChatBotMessage(chatResponse, {
      widget: 'chatWidget',
      payload: {
        ...parsedResponse, // send chat response to payload
      }, // Sending API response as payload for static use
      withAvatar: true,
    })
  );

  // Set the sources array
  if (chatResponse.sources && chatResponse.sources.length > 0) {
    setResponseDetailsSources(chatResponse.sources);
  }

  // Add the sources to sourceHistory for new message if it has sources
  if (chatResponse && chatResponse.responseId && chatResponse.sources) {
    console.log('Sources received:', chatResponse.sources); // Debugging line
    // Use functional form of setSourceHistory to ensure we're working with the most recent state
    setSourceHistory((prevSourceHistory: any) => ({
      ...prevSourceHistory,
      [chatResponse.responseId]: chatResponse.sources,
    }));
    setSelectedResponseId(chatResponse.responseId);
  }

  setState((prev: any) => {
    if (isChatHistoryActive) {
      const stateMsgs = prev.messages;
      const prompt = stateMsgs[stateMsgs.length - 2]; // prompt details
      /*
        update ChatWindowConversations state with prompt and its response. which indirectly updates the internal chatbot state which handled in ActionProvider useEffects
        No welcome msg and starter questions should be displayed.
        */
      setChatWindowConversations((state: any) => [...state, prompt, ...botMessages]);
    } else {
      /*
      Show the bot messages in ChatWindowConversations state with welcome msg and starter questions.
      */
      setChatWindowConversations(() => {
        return [...prev.messages.slice(0, -1), ...botMessages];
      });
    }
    //reset the ChatResponse once after prompt response is received
    //helps in avoid conflicts
    // setChatResponse({});
    return prev;
  });

  // Scroll to bottom of chatbot, delay of 1 second
  scrollToBottom('', 1000);
};

// Function to handle the Step Response (intermediate steps)
export const stepResponseHelper = (chatbotResources: ChatbotResources, parsedResponse: any) => {
  // Gather the resources
  const { setState, createChatBotMessage } = chatbotResources;

  if (parsedResponse?.stepResponse || typeof parsedResponse === 'string' || parsedResponse instanceof String) {
    // No final response yet, step instead
    // Update the API status widget
    setState((prev: any) => ({
      ...prev,
      intIntent: parsedResponse?.stepResponse?.agentIntent || prev.intIntent,
      intPlan: parsedResponse?.stepResponse?.agentPlan || prev.intPlan,
      currentStep:
        parsedResponse?.stepResponse?.currentStep ||
        (typeof parsedResponse === 'string' || parsedResponse instanceof String ? parsedResponse : prev.currentStep),
    }));
    // Scroll to bottom of chatbot, delay of .1 second
    scrollToBottom('', 100);
  } else {
    // No final response, no step, show error message
    const botErrorMessage = createChatBotMessage(
      "I'm sorry. An error has occurred. Please try rephrasing your request.",
      {}
    );

    // Update the message history to show
    setState((prev: any) => ({
      ...prev,
      messages: [...prev.messages.slice(0, -1), botErrorMessage], // include image message if it exists
      intIntent: '',
      intPlan: [],
      currentStep: '',
    }));
    // Scroll to bottom of chatbot, delay of 1 second
    scrollToBottom('', 1000);
  }
};

export const cancelChatResponseHelper = async (
  { selectedTranscript, websocket, prompt, instanceId, currentGenArcPromptAnalyticsUId }: ChatbotResources,
  enterpriseId: string,
  prompts: any,
  openAi: boolean,
  genAi: boolean,
  chatSessionId: string
) => {
  const isPreDefinedPrompt = getPromptIdFromName(prompts, prompt) ? true : false;
  const token = await getValidToken();
  const request = {
    function: FunctionType.CANCELRESPONSE,
    prompt: prompt,
    userId: localStorage.getItem('gamma-userid') || crypto.randomUUID(),
    LoggedInUser: enterpriseId,
    PreDefinedPromptUId: getPromptIdFromName(prompts, prompt),
    UserPrompt: isPreDefinedPrompt ? null : prompt,
    OpenAI: openAi,
    GenAI: genAi,
    SessionUId: chatSessionId,
    GenArcPromptAnalyticsUId: currentGenArcPromptAnalyticsUId,
    sourceName: selectedTranscript,
    is_prompt_cancelled: true,
    instance_id: instanceId,
    authorization: `Bearer ${token}`,
  };
  console.log('reqObj for chat terminate', request);
  websocket?.send('SendMessage', request);
};

export const handlePostCancelResponseHelper = ({
  setState,
  createChatBotMessage,
  setChatWindowConversations,
  setInstanceId,
  setCurrentGenArcPromptAnalyticsUId,
  promptCancellationStatus,
  setPromptCancellationStatus,
}: ChatbotResources) => {
  const responseMessage =
    promptCancellationStatus === STATUS.SUCCESS
      ? '_Your Prompt Response has been cancelled._'
      : '_Couldnt cancel your request._';
  const botMessages = [createChatBotMessage({ text: responseMessage })];
  setState((prev: any) => {
    /*
     * Update the chatWindowConversation state with cancel message,
     * which indirectly updates the internal chatbot state which handled in ActionProvider useEffects
     */
    setChatWindowConversations(() => {
      return [...prev.messages.slice(0, -1), ...botMessages];
    });
    return prev;
  });
  /*
   * set instanceId and currentGenArcPromptAnalyticsUId to empty string(reset values),
   * since prompt cancellation is completed
   */
  setInstanceId('');
  setCurrentGenArcPromptAnalyticsUId('');
  setPromptCancellationStatus(STATUS.DEFAULT);
};
